import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PenLine, MapPin, Clock, Users, CheckCircle2, Circle, ArrowRight, Camera } from "lucide-react";
import { upcomingSigningDays } from "../lib/api";
import { PhotoGuide } from "./estate/PhotoGuide";
import { tr } from "../lib/i18n";

// One tap on signing day: photograph the signed pages → filed as the signed copy (PDF) → compare step.
export const PhotoProofButton = ({ kind, label = "Snap the signed pages", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`photo-proof-btn-${kind}`} className={`inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:bg-primary/90 ${className}`}>
        <Camera className="h-3.5 w-3.5" /> {tr(label)}
      </button>
      {open && <PhotoGuide kind={kind} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

const whenLabel = (d) => (d.is_today ? tr("Today") : d.days_until === 1 ? tr("Tomorrow") : `${tr("In")} ${d.days_until} ${tr("days")}`);

const DayCard = ({ d }) => (
  <div data-testid={`signing-day-card-${d.kind}`} className={`rounded-2xl border p-4 ${d.is_today ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="h-8 w-8 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><PenLine className="h-4 w-4" /></span>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-widest font-mono text-primary">{whenLabel(d)} · {tr("Signing day")}</div>
        <div className="font-serif text-xl leading-tight" data-testid={`signing-day-title-${d.kind}`}>{tr(d.label)}</div>
      </div>
      <Link to={d.href} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`signing-day-open-${d.kind}`}>{tr("Open the draft")} <ArrowRight className="h-3 w-3" /></Link>
    </div>
    <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-3.5 w-3.5" /> <span>{d.date}{d.time ? ` · ${d.time}` : ""}</span></div>
        <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> <span>{d.place || tr("Place to be confirmed")}</span></div>
        {d.parties.length > 0 && (
          <div className="flex items-start gap-2 text-muted-foreground"><Users className="h-3.5 w-3.5 mt-0.5" />
            <ul className="space-y-0.5">{d.parties.map((p, i) => <li key={i} className="flex items-center gap-1.5">{p.confirmed ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <Circle className="h-3 w-3" />}<span>{p.name || tr("Unnamed")} <span className="text-xs">· {tr(p.role)}</span></span></li>)}</ul>
          </div>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{tr("Bring")}</div>
        <ul className="mt-1 space-y-1 text-xs">{d.bring.map((b) => <li key={b} className="flex gap-2"><Circle className="h-3 w-3 mt-0.5 shrink-0 text-muted-foreground" /><span>{tr(b)}</span></li>)}</ul>
      </div>
    </div>
    {d.is_today && (
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <PhotoProofButton kind={d.kind} />
        <span>{tr("After everyone has signed, photograph each page — they're filed as the signed copy and checked against the draft.")}</span>
      </div>
    )}
  </div>
);

// Morning-of checklist for any estate-document signing within the next week.
export const SigningDayCard = () => {
  const { data } = useQuery({ queryKey: ["signing-days"], queryFn: upcomingSigningDays, staleTime: 60_000 });
  if (!data?.items?.length) return null;
  return <div className="mb-4 space-y-3" data-testid="signing-day-cards">{data.items.map((d) => <DayCard key={d.kind} d={d} />)}</div>;
};
